import ClientApiScreens, {
  getImplementationScreens,
  isJourneyEndScreen,
} from '../Data/ClientApiScreens';

export const START_POSITION = { stepIndex: 0, screenIndex: 0 };

// A dropped leading step leaves the sequence starting past stepIndex 0, so the
// journey must open on the first screen that survived, not a fixed (0,0).
export const firstPosition = (sequence = []) =>
  sequence.length === 0
    ? START_POSITION
    : {
        stepIndex: sequence[0].stepIndex,
        screenIndex: sequence[0].screenIndex,
      };

/**
 * Flattens the dag's ordered steps into the screens the customer moves through,
 * each carrying the step it belongs to so the screen can read and write that
 * step's state.
 */
export const buildScreenSequence = (steps = []) =>
  steps.flatMap(({ key, implementation }, stepIndex) =>
    (getImplementationScreens(implementation) ?? []).map(
      (screenId, screenIndex) => ({
        stepIndex,
        screenIndex,
        stepKey: key,
        screenId,
      })
    )
  );

const indexOfPosition = (sequence, position) =>
  (sequence ?? []).findIndex(
    (screen) =>
      screen.stepIndex === position?.stepIndex &&
      screen.screenIndex === position?.screenIndex
  );

const toPosition = ({ stepIndex, screenIndex }) => ({ stepIndex, screenIndex });

export const screenAtPosition = (sequence, position) => {
  const index = indexOfPosition(sequence, position);
  return index < 0 ? undefined : sequence[index];
};

export const nextPosition = (sequence, position) => {
  const index = indexOfPosition(sequence, position);
  if (index < 0 || index >= sequence.length - 1) return position;
  return toPosition(sequence[index + 1]);
};

export const prevPosition = (sequence, position) => {
  const index = indexOfPosition(sequence, position);
  if (index <= 0) return position;
  return toPosition(sequence[index - 1]);
};

/** Whether the current screen ends the journey, wherever it sits. */
export const isOnJourneyEndScreen = (sequence, position) =>
  isJourneyEndScreen(screenAtPosition(sequence, position)?.screenId);

export const selectCurrentScreen = (state) =>
  screenAtPosition(
    state.clientApiJourney?.screenSequence,
    state.clientApiJourney?.position
  );

export const selectCurrentStepKey = (state) =>
  selectCurrentScreen(state)?.stepKey;

/**
 * Where the customer is in the journey: every screen they see is one stage.
 * Returns { currentIndex, total }, or undefined before there is a current screen.
 */
export const selectJourneyProgress = (state) => {
  const sequence = state.clientApiJourney?.screenSequence ?? [];
  const index = indexOfPosition(sequence, state.clientApiJourney?.position);
  if (index < 0) return undefined;

  return { currentIndex: index, total: sequence.length };
};

const LOCATION_SCREEN_IDS = [
  ClientApiScreens.LOCATION_LIST,
  ClientApiScreens.ADDRESS_LIST,
];

/**
 * The step key of the location screen nearest before the current position: the
 * step whose chosen location a slot picker lists slots for.
 */
const findUpstreamLocationScreen = (state) => {
  const sequence = state.clientApiJourney?.screenSequence ?? [];
  const index = indexOfPosition(sequence, state.clientApiJourney?.position);
  if (index < 0) {
    return undefined;
  }
  return sequence
    .slice(0, index)
    .reverse()
    .find((screen) => LOCATION_SCREEN_IDS.includes(screen.screenId));
};

export const selectUpstreamLocationStepKey = (state) =>
  findUpstreamLocationScreen(state)?.stepKey;

/** The step key of the SolarPotential step in a screen sequence, if present. */
export const findSolarStepKey = (screenSequence = []) =>
  screenSequence.find(
    (screen) => screen.screenId === ClientApiScreens.SOLAR_POTENTIAL
  )?.stepKey;

/**
 * Whether this is the address-list (home-visit) journey rather than a store or
 * clinic one: an ADDRESS_LIST screen appears anywhere in the sequence. Scans the
 * whole sequence, so it holds on any screen regardless of its position.
 */
export const selectIsAddressListJourney = (state) =>
  (state.clientApiJourney?.screenSequence ?? []).some(
    (screen) => screen.screenId === ClientApiScreens.ADDRESS_LIST
  );
